/**
 * Downhole Tools — the payloads of /api/v1/downhole/* (fieldiq-api#882, #883).
 *
 * TYPED TO THE ACTUAL PAYLOAD, read off `downhole.routes.ts`, not off the
 * unrouted screens. The screens were written against a table family that never
 * existed (`component_type`, `total_cd_hours`, `dispatched_at`, a `job` on an
 * assembly); the API returns the `dh_*` columns and the screens adapt.
 *
 * Every response is `{ success, data, meta }`. `meta` carries the tenant's
 * unit_system, currency and time zone, and every screen formats through it —
 * see lib/downhole-units.ts. Numerics arrive as JSON numbers, instants as
 * ISO-8601, `date` columns as 'YYYY-MM-DD'. Any of them may be null.
 *
 * UNITS. Every measured column is IMPERIAL as stored (in, ft, lbs, °F, klbs,
 * ft·lbs). The field names carry the unit where the column does
 * (`length_ft`, `weight_lbs`); `od`, `id_inner`, `fish_neck_od` and
 * `hole_size` are inches.
 */

export const DH_COMPONENT_STATUSES = ['available', 'in_assembly', 'in_hole', 'maintenance', 'failed', 'retired'] as const;
export type DhComponentStatus = (typeof DH_COMPONENT_STATUSES)[number];

export const DH_ASSEMBLY_STATUSES = ['building', 'ready', 'in_hole', 'laid_down', 'disassembled'] as const;
export type DhAssemblyStatus = (typeof DH_ASSEMBLY_STATUSES)[number];

export const DH_WORK_ORDER_STATUSES = ['open', 'in_progress', 'awaiting_parts', 'completed', 'cancelled'] as const;
export type DhWorkOrderStatus = (typeof DH_WORK_ORDER_STATUSES)[number];

/** api#910: `void` joined the vocabulary — a voided invoice frees its runs to be billed again. */
export const DH_INVOICE_STATUSES = ['draft', 'sent', 'approved', 'paid', 'void'] as const;
export type DhInvoiceStatus = (typeof DH_INVOICE_STATUSES)[number];

/** `dh_component_category` — 0144, extended by 0152c and 0443. 22 members. */
export const DH_COMPONENT_CATEGORIES = [
  'drill_collar', 'stabilizer', 'jar', 'crossover_sub', 'float_sub', 'mud_motor', 'mwd_tool', 'lwd_tool',
  'reamer', 'hole_opener', 'shock_sub', 'non_mag_collar', 'bit_sub', 'pup_joint', 'other',
  'float_collar', 'centralizer', 'shoe', 'sub',
  'packer', 'agitator', 'fishing_tool',
] as const;
export type DhComponentCategory = (typeof DH_COMPONENT_CATEGORIES)[number];

/** dh_work_orders.work_type (0150 CHECK). */
export const DH_WORK_TYPES = ['inspection', 'repair', 'recertification', 'hardbanding', 'magnaflux', 'other'] as const;

export const DH_BHA_TYPES = ['rotary', 'motor', 'rss', 'fishing', 'coring', 'washover', 'other'] as const;
export type DhBhaType = (typeof DH_BHA_TYPES)[number];

/** companies.unit_system. Null only when the company row has none. */
export type DhUnitSystem = 'metric' | 'imperial';

export interface DownholeMeta {
  unit_system: DhUnitSystem | null;
  currency: string | null;
  time_zone: string | null;
  /** The tenant's calendar day, 'YYYY-MM-DD'. */
  today: string | null;
  /** /components/alerts only: the threshold the list was cut at. */
  threshold?: number;
}

/** The body of every /downhole response, as the API sends it. */
export interface DownholeEnvelope<T> {
  success: boolean;
  data: T;
  meta: DownholeMeta;
}

/** A downhole read: the payload and the tenant settings it must be formatted with. */
export interface DownholeResult<T> {
  data: T;
  meta: DownholeMeta;
}

/**
 * GET/PUT /downhole/settings (api#1052, 0510). One key today. No dh_settings row
 * reads as `false` API-side, so `false` here is a real answer, not a gap.
 */
export interface DhSettings {
  dd_mwd_enabled: boolean;
  /**
   * api#1062: the company's default standby share of a day rate, a FRACTION
   * (0.6 = 60%). Present only while the mode is on; null = not set.
   */
  default_standby_pct?: number | null;
  /**
   * api#1126 (0525): hours a run may stay open before the action queue flags
   * it. Always sent, mode on or off; the API defaults it to 120.
   */
  open_run_alert_hours: number;
}

/** PUT /downhole/settings. The API refuses any other key (UNKNOWN_FIELD). */
export interface DhSettingsWrite {
  dd_mwd_enabled?: boolean;
  /** A fraction; null clears it. Refused (DD_MODE_OFF) unless the mode is, or is being turned, on. */
  default_standby_pct?: number | null;
  /** Whole hours, 1–2000 (api#1126). */
  open_run_alert_hours?: number;
}

export interface DhDashboard {
  total_components: number;
  components_by_status: Record<DhComponentStatus, number>;
  active_assemblies: number;
  assemblies_by_status: Record<DhAssemblyStatus, number>;
  open_work_orders: number;
  overdue_work_orders: number;
  pending_invoices: number;
  invoices_by_status: Record<DhInvoiceStatus, number>;
  /** Null when outstanding invoices are in more than one currency. */
  outstanding_invoice_total: number | null;
  outstanding_invoice_currency: string | null;
  wear_alerts: number;
}

export interface DhComponent {
  id: string;
  serial_number: string;
  category: string;
  description: string | null;
  status: string;
  od: number | null;
  id_inner: number | null;
  length_ft: number | null;
  weight_lbs: number | null;
  connection_top: string | null;
  connection_bottom: string | null;
  manufacturer: string | null;
  model: string | null;
  year_manufactured: number | null;
  date_manufactured: string | null;
  max_temp_f: number | null;
  max_wob_klbs: number | null;
  fish_neck_od: number | null;
  pressure_rating: string | number | null;
  last_inspection: string | null;
  last_inspection_result: string | null;
  next_inspection_due: string | null;
  box_thread_condition: string | null;
  pin_thread_condition: string | null;
  total_runs: number | null;
  total_run_hours: number | null;
  total_footage_ft: number | null;
  wear_limit_hours: number | null;
  wear_limit_runs: number | null;
  /** Wear USED, percent of the hours limit. Null when there is no limit. */
  wear_percent_hours: number | null;
  /** Wear USED, percent of the runs limit. Null when there is no limit. */
  wear_percent_runs: number | null;
  location: string | null;
  notes: string | null;
  is_active: boolean;
  qr_code_url: string | null;
  dope_clean_confirmed: boolean | null;
  dope_clean_confirmed_at: string | null;
  return_arrived_at: string | null;
  available_at: string | null;
  turnaround_hours: number | null;
  created_at: string;
  updated_at: string;
  /** List only: the assembly it is in now, through the active membership. */
  current_assembly_id?: string | null;
  current_assembly_number?: string | null;
}

export type DhAlertReason = 'wear_hours' | 'wear_runs' | 'inspection_overdue';

export interface DhComponentAlert extends DhComponent {
  /** The worse of the two wear figures; null when the alert is inspection-only. */
  wear_percent_max: number | null;
  inspection_overdue: boolean;
  reasons: DhAlertReason[];
}

export interface DhComponentAssemblyHistory {
  assembly_id: string;
  assembly_number: string | null;
  assembly_name: string | null;
  /** The assembly's status NOW, not during this membership. */
  assembly_status: string | null;
  well_name: string | null;
  rig_name: string | null;
  position: number | null;
  installed_at: string | null;
  removed_at: string | null;
  condition_on_return: string | null;
  cd_hours_this_run: number | null;
}

export interface DhComponentRunHistory {
  id: string;
  assembly_id: string;
  assembly_number: string | null;
  assembly_name: string | null;
  run_number: number | null;
  well_name: string | null;
  rig_name: string | null;
  run_in_at: string | null;
  run_out_at: string | null;
  depth_in_ft: number | null;
  depth_out_ft: number | null;
  footage_drilled: number | null;
  circulating_hours: number | null;
  rotating_hours: number | null;
  lih_flag: boolean | null;
  lost_in_hole_here: boolean | null;
}

export interface DhWorkOrder {
  id: string;
  wo_number: string | null;
  component_id?: string;
  component_serial?: string | null;
  component_category?: string | null;
  component_description?: string | null;
  work_type: string | null;
  status: string;
  priority: string | null;
  description: string | null;
  findings: string | null;
  resolution: string | null;
  /** A user id. Render `assigned_to_name`, never this. */
  assigned_to: string | null;
  assigned_to_name: string | null;
  labor_hours: number | null;
  parts_cost: number | null;
  total_cost: number | null;
  /**
   * The parts on the order (api#905), `{ part, qty, cost }` with `cost` PER
   * UNIT in the tenant's currency. `parts_cost` is the API's total of it —
   * never summed here. NULL when no parts list was ever written.
   */
  parts_list?: DhPartLine[] | null;
  started_at: string | null;
  completed_at: string | null;
  due_date: string | null;
  created_at: string;
  updated_at?: string;
  /**
   * The tool's status, on a WRITE's answer only (api#905 loadWorkOrder). The
   * GET reads do not carry it, so a screen that re-reads must not show it.
   */
  component_status?: string | null;
}

/** dh_work_orders.work_type values that are inspections (api#905): completing one needs pass/fail. */
export const DH_INSPECTION_WORK_TYPES = ['inspection', 'recertification', 'magnaflux'] as const;

/** The api#905 transition map, mirrored so no status button is dead. completed and cancelled are final. */
export const DH_WORK_ORDER_TRANSITIONS: Record<string, readonly string[]> = {
  open: ['in_progress', 'awaiting_parts', 'completed', 'cancelled'],
  in_progress: ['awaiting_parts', 'completed', 'cancelled'],
  awaiting_parts: ['in_progress', 'completed', 'cancelled'],
  completed: [],
  cancelled: [],
};

/** One line of a work order's parts list (api#905 parsePartsList): qty > 0, cost per unit ≥ 0. */
export interface DhPartLine {
  part: string;
  qty: number;
  cost: number;
}

/** api#905 `dh_work_orders.priority` (DH_WO_PRIORITIES). */
export const DH_WO_PRIORITIES = ['low', 'normal', 'high', 'critical'] as const;

/**
 * A work-order write: PATCH /work-orders/:id, and the open on a component
 * (POST /components/:id/work-orders and /inspections). `parts_list` is the
 * whole list — it replaces what is there, and `null` clears it.
 */
export type DhWorkOrderWrite = Record<string, string | number | boolean | null | DhPartLine[]>;

export const DH_DISASSEMBLY_REASONS = ['td_reached', 'failure', 'planned_change', 'end_of_well', 'other'] as const;
export const DH_ITEM_CONDITIONS = ['good', 'serviceable', 'needs_repair', 'failed', 'scrap'] as const;
export const DH_RECOMMEND_ACTIONS = ['return_to_stock', 'inspect', 'repair', 'scrap'] as const;

export interface DhDisassembleItemWrite {
  component_id: string;
  condition: string;
  recommend_action: string;
  box_thread_condition?: string | null;
  pin_thread_condition?: string | null;
  damage_notes?: string | null;
  wear_notes?: string | null;
  /** INCHES. */
  od_measurement?: number | null;
  dope_clean_confirmed?: boolean;
}

export interface DhDisassembleWrite {
  reason: string;
  location?: string | null;
  notes?: string | null;
  items: DhDisassembleItemWrite[];
}

/** POST /assemblies/:id/disassemble — the record and what happened to each tool. */
export interface DhDisassemblyResult {
  id: string;
  assembly_id: string;
  disassembled_at: string;
  reason: string;
  items: {
    id: string;
    component_id: string;
    serial_number: string;
    component_status: string;
    position: number;
    condition: string;
    recommend_action: string;
    work_order_id: string | null;
    work_order_number: string | null;
  }[];
}

export interface DhComponentDisassemblyHistory {
  disassembly_id: string;
  assembly_id: string;
  assembly_number: string | null;
  disassembled_at: string | null;
  reason: string | null;
  condition: string | null;
  wear_notes: string | null;
  /** Inches, as stored. */
  od_measurement: number | null;
  recommend_action: string | null;
}

export interface DhComponentDetail extends DhComponent {
  assembly_history: DhComponentAssemblyHistory[];
  run_history: DhComponentRunHistory[];
  work_orders: DhWorkOrder[];
  disassembly_history: DhComponentDisassemblyHistory[];
}

/** One component in an assembly's string (GET /assemblies/:id, and every write's response). */
export interface DhAssemblyMember {
  assembly_component_id: string;
  component_id: string;
  position: number;
  installed_at: string | null;
  removed_at: string | null;
  /** ft·lbs, as stored. */
  makeup_torque: number | null;
  condition_on_return: string | null;
  cd_hours_this_run: number | null;
  assembly_notes: string | null;
  serial_number: string;
  category: string;
  description: string | null;
  component_status: string;
  od: number | null;
  id_inner: number | null;
  length_ft: number | null;
  connection_top: string | null;
  connection_bottom: string | null;
  total_runs: number | null;
  total_run_hours: number | null;
  total_footage_ft: number | null;
  wear_percent_hours: number | null;
  wear_percent_runs: number | null;
}

/** dh_assemblies ticket_summary (api#928). Counts are the API's. */
export interface DhAssemblyTicketSummary {
  /** Closed (run out) and on no live ticket. */
  closed_runs_without_ticket: number;
  /** Office-approved, not yet sent to the customer. */
  tickets_ready_to_send: number;
  /** Office-approved and sent; the customer is next. */
  tickets_awaiting_customer: number;
  /** On a customer-approved ticket, on no live invoice. */
  runs_approved_not_invoiced: number;
  run_open: boolean;
}

export interface DhAssembly {
  id: string;
  assembly_number: string;
  name: string;
  status: string;
  /**
   * api#910: the two-letter province/state the work is in (CHECK ^[A-Z]{2}$).
   * Copied onto each run when the run is logged; tax on an invoice follows the
   * RUNS', so a later change here does not move runs already logged. NULL falls
   * back to the company's home province at invoicing.
   */
  province_state?: string | null;
  template_id: string | null;
  template_name: string | null;
  customer_id: string | null;
  customer_name: string | null;
  well_name: string | null;
  rig_name: string | null;
  /** Inches, as stored. */
  hole_size: number | null;
  /** Recomputed from the string by the API; NULL if any member has no length. */
  total_length_ft: number | null;
  built_by: string | null;
  built_by_name: string | null;
  built_at: string | null;
  laid_down_at: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  component_count: number;
  run_count: number | null;
  first_run_in_at: string | null;
  last_run_out_at: string | null;
  total_footage_ft: number | null;
  total_circulating_hours: number | null;
  /**
   * API-A (api#928): where the assembly's runs stand on the way to a bill.
   * Optional — absent before that deploy, and a screen then derives its own
   * indicators from the runs read.
   */
  ticket_summary?: DhAssemblyTicketSummary | null;
  /**
   * DD/MWD batch 4 (api#1062): the well job the assembly is on, and its number.
   * THREE STATES, NOT TWO. Absent: the tenant's dd_mwd mode is off or the API
   * predates batch 4, so the link is UNKNOWN. null: on no job. A uuid: on that
   * job. `?? null` would turn "unknown" into "on no job", so never default it.
   */
  dh_job_id?: string | null;
  job_number?: string | null;
}

export interface DhAssemblyDetail extends DhAssembly {
  /** The current string, by position. Empty once the assembly is broken down. */
  components: DhAssemblyMember[];
  removed_components: DhAssemblyMember[];
}

export interface DhTemplateSlot {
  id?: string;
  slot_number: number;
  category: string;
  label: string | null;
  required: boolean;
  notes: string | null;
}

export interface DhTemplate {
  id: string;
  name: string;
  description: string | null;
  bha_type: string | null;
  /** Inches, as stored. */
  hole_size: number | null;
  total_slots: number | null;
  well_type: string | null;
  formation: string | null;
  customer_id: string | null;
  customer_name: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  slot_count: number;
}

export interface DhTemplateDetail extends DhTemplate {
  slots: DhTemplateSlot[];
}

/** POST /assemblies and PATCH /assemblies/:id. `null` clears a field; an absent key leaves it. */
export interface DhAssemblyWrite {
  name?: string;
  customer_id?: string | null;
  template_id?: string | null;
  well_name?: string | null;
  rig_name?: string | null;
  /** Inches — convert a metric input with toStoredImperial first. */
  hole_size?: number | null;
  notes?: string | null;
  status?: string;
  /** Two-letter code (AB, TX …) or null. */
  province_state?: string | null;
}

/**
 * A component write body (api#900). Keys are the API's; measures IMPERIAL.
 * Opening balances (total_run_hours, total_runs, total_footage_ft,
 * last_inspection) are accepted on create only. `status` on update only, and
 * only 'available' or 'retired'.
 */
export type DhComponentWrite = Record<string, string | number | boolean | null>;

export interface DhComponentImportResult {
  inserted: { row: number; id: string; serial_number: string }[];
  /** Same serial, same category as a tool already in the register (or earlier in the file). */
  skipped: { row: number; serial_number: string; reason: 'duplicate'; existing_id: string; existing_row?: number }[];
  /** Same serial, DIFFERENT category: not inserted; the API leaves the call to a person. */
  review: {
    row: number;
    serial_number: string;
    category: string;
    existing_id: string;
    existing_serial_number: string;
    existing_category: string;
    existing_row?: number;
  }[];
  counts: { inserted: number; skipped: number; review: number };
  message: string;
}

/** GET /components/:id/qr — the label for one tool. */
export interface DhComponentQr {
  component_id: string;
  serial_number: string;
  category: string;
  description: string | null;
  od: number | null;
  manufacturer: string | null;
  company_name: string | null;
  /** The component's page in the tenant app — what the code encodes. */
  target_url: string;
  png_data_url: string;
  svg: string;
}

export interface DhTemplateWrite {
  name: string;
  bha_type: string;
  description?: string | null;
  customer_id?: string | null;
  /** Inches. */
  hole_size?: number | null;
  well_type?: string | null;
  formation?: string | null;
  slots: { slot_number: number; category: string; label?: string | null; required?: boolean; notes?: string | null }[];
}

/** One run (GET /runs, GET /assemblies/:id/runs). Measured values imperial as stored. */
export interface DhRun {
  id: string;
  assembly_id: string;
  assembly_number: string | null;
  assembly_name: string | null;
  run_number: number | null;
  well_name: string | null;
  rig_name: string | null;
  customer_id: string | null;
  customer_name: string | null;
  field_tech_id: string | null;
  field_tech_name: string | null;
  run_in_at: string | null;
  run_out_at: string | null;
  depth_in_ft: number | null;
  depth_out_ft: number | null;
  footage_drilled: number | null;
  rotating_hours: number | null;
  circulating_hours: number | null;
  on_bottom_hours: number | null;
  /** ft/hr */
  rop_avg: number | null;
  wob_avg_klbs: number | null;
  rpm_avg: number | null;
  torque_avg_ftlbs: number | null;
  /** ppg */
  mud_weight: number | null;
  /** US gal/min */
  flow_rate_gpm: number | null;
  bit_condition: string | null;
  failure_mode: string | null;
  notes: string | null;
  lih_flag: boolean | null;
  lih_component_id: string | null;
  lih_component_serial: string | null;
  billing_type: string | null;
  minimum_charge_applied: boolean | null;
  minimum_charge_amount: number | null;
  /**
   * api#902, derived from the two instants: `open` (in, not out), `closed`
   * (both), `not_run` (mobilized, never run in — neither).
   */
  run_state?: DhRunState | null;
  /** run_out_at − run_in_at in hours, the API's figure. Null unless closed. */
  cd_hours?: number | null;
  /**
   * api#913 billing state, on every run read. `invoiced`: on a LIVE (non-void)
   * invoice — PATCH then takes notes only. `ever_invoiced`: billed at any
   * time, void included — DELETE is refused for good. Optional because a
   * response from before #913 carries neither; absent is "not known", never
   * "not invoiced".
   */
  invoiced?: boolean;
  ever_invoiced?: boolean;
  /** api#910: the run's province/state, snapshotted from its assembly at creation. Tax follows it. */
  province_state?: string | null;
  invoice_id?: string | null;
  invoice_number?: string | null;
  /**
   * 0461 (api#923): the field ticket the run is on, if any. A run is billed
   * only once its ticket is `customer_approved` (409 RUN_NOT_CUSTOMER_APPROVED
   * otherwise). Optional: a read from before 0461 carries none — "not known".
   */
  dh_ticket_id?: string | null;
  ticket_number?: string | null;
  ticket_status?: DhTicketStatus | null;
  created_at: string;
  updated_at: string;
}

/* ─────────────────────────── field tickets (0461) ─────────────────────────── */

/** dh_tickets.status (api#923 / #925), the API's own list. */
export const DH_TICKET_STATUSES = ['draft', 'office_approved', 'customer_approved', 'customer_rejected'] as const;
export type DhTicketStatus = (typeof DH_TICKET_STATUSES)[number];

/**
 * dh_tickets.customer_email_status (0461 CHECK). Moves forward only
 * (not_sent → pending → sent → delivered → opened → clicked); failed is the
 * send's own refusal; bounced and complained are terminal (API-3 webhook).
 */
export const DH_TICKET_EMAIL_STATUSES = [
  'not_sent', 'pending', 'sent', 'failed', 'bounced', 'delivered', 'complained', 'opened', 'clicked',
] as const;
export type DhTicketEmailStatus = (typeof DH_TICKET_EMAIL_STATUSES)[number];

/**
 * A field ticket as the list and the detail return it (TICKET_COLUMNS). Read
 * as stored: snake_case, money as numbers, instants ISO. The prices are a
 * SNAPSHOT taken at office approval — null on a draft, never a guess.
 */
export interface DhTicket {
  id: string;
  ticket_number: string;
  status: DhTicketStatus;
  /** null on a daily ticket (scope 'job_day'), which bills a day of a well job, not an assembly. */
  assembly_id: string | null;
  assembly_number: string | null;
  assembly_name: string | null;
  customer_id: string | null;
  customer_name: string | null;
  well_name: string | null;
  rig_name: string | null;
  lsd: string | null;
  afe_number: string | null;
  po_number: string | null;
  province_state: string | null;
  currency: string | null;
  subtotal: number | null;
  tax_rate_1_label: string | null;
  tax_rate_1_amount: number | null;
  tax_rate_2_label: string | null;
  tax_rate_2_amount: number | null;
  total_amount: number | null;
  customer_notes: string | null;
  notes: string | null;
  office_approved_by: string | null;
  office_approved_at: string | null;
  sent_to_customer_at: string | null;
  customer_approved_by_name: string | null;
  customer_approved_at: string | null;
  /** 'link' (the company man on the review page) or 'office_upload' (a stamped copy recorded by the office). */
  customer_approval_source: 'link' | 'office_upload' | null;
  customer_rejection_reason: string | null;
  customer_email_status: DhTicketEmailStatus | null;
  /** API-A: the address the last send went to, and what it copied. Absent before 0463. */
  customer_email_sent_to?: string | null;
  customer_email_cc?: string[] | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  run_count: number;
  /**
   * Per-assembly ticket: the runs on it. Daily ticket (api, PR pending): the
   * day's runs. Run numbers are PER ASSEMBLY, so on a daily ticket two BHAs can
   * both show 1: label runs from `runs[]` (assembly_number + run_number).
   */
  run_numbers: number[];
  invoiced_run_count: number;
  /**
   * api#1090: the live (non-void) invoice a DAILY ticket is billed on, null
   * when unbilled. A per-assembly ticket carries neither key (its runs carry
   * their own invoice), so absent is "not this kind of ticket", not "unbilled".
   */
  invoice_id?: string | null;
  invoice_number?: string | null;
  /**
   * api#1062 daily tickets. Present only when scope is 'job_day'; a
   * per-assembly ticket's response carries none of these keys.
   */
  scope?: 'assembly' | 'job_day';
  dh_job_id?: string | null;
  job_number?: string | null;
  /** The day it bills, 'YYYY-MM-DD'. */
  work_date?: string | null;
}

/** A daily ticket: one day of a Directional & MWD well job (api#1062). */
export const isDailyTicket = (t: Pick<DhTicket, 'scope'> | null | undefined): boolean => t?.scope === 'job_day';

/** One signed line of the snapshot (dh_ticket_line_items). */
export interface DhTicketLine {
  id: string;
  /** null on a daily ticket's line, which bills a crew member's or a tool's day. */
  run_log_id: string | null;
  /** Daily ticket lines (api#1062). */
  dh_job_crew_id?: string | null;
  personnel_role?: string | null;
  assembly_id?: string | null;
  run_number: number | null;
  component_id: string | null;
  serial_number: string | null;
  line_type: string;
  description: string | null;
  quantity: number | null;
  unit: string | null;
  rate: number | null;
  amount: number | null;
  minimum_applied: boolean | null;
  sort_order: number;
}

export interface DhTicketDetail extends DhTicket {
  runs: DhRun[];
  line_items: DhTicketLine[];
}

/** POST /downhole/tickets and PATCH /downhole/tickets/:id. Unknown keys are a 400. */
export interface DhTicketWrite {
  assembly_id?: string;
  run_ids?: string[];
  lsd?: string | null;
  afe_number?: string | null;
  po_number?: string | null;
  customer_notes?: string | null;
  notes?: string | null;
}

/**
 * A rate card row (api#906, dh_rates 0452). Keys exactly one of `category` /
 * `component_id`; `customer_id` null is the company default. Every rate is
 * nullable — NULL is "not quoted", never $0. `footage_rate` is per FOOT, as
 * stored. Dates are calendar days, both ends inclusive.
 */
export interface DhRate {
  id: string;
  customer_id: string | null;
  customer_name: string | null;
  category: string | null;
  component_id: string | null;
  component_serial: string | null;
  run_rate: number | null;
  hourly_rate: number | null;
  day_rate: number | null;
  footage_rate: number | null;
  minimum_charge: number | null;
  lih_rate: number | null;
  currency: 'CAD' | 'USD' | string;
  effective_date: string;
  expiry_date: string | null;
  notes: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  /**
   * api#1062 daily-billing prices, written only while the dd_mwd mode is on.
   * Standby is a day rate OR a fraction of the day rate (0.6 = 60%), never
   * both (STANDBY_ONE_ONLY). Optional: a read from before batch 4 has none,
   * which is not "not quoted".
   */
  standby_day_rate?: number | null;
  standby_pct?: number | null;
  mobilization_charge?: number | null;
  demobilization_charge?: number | null;
}

export type DhRateWrite = Record<string, string | number | null>;

/** GET /runs/:id/price — priceRun (lib/downhole-pricing.ts on the API). */
export interface DhPriceLine {
  component_id: string;
  serial_number: string;
  category: string;
  rate_id: string | null;
  rate_scope: 'component_customer' | 'category_customer' | 'component_default' | 'category_default' | null;
  line_type: string;
  unit: string;
  description: string;
  quantity: number | null;
  rate: number | null;
  amount: number | null;
  minimum_applied: boolean;
  currency: string | null;
  unpriced_reason?: string;
}

export interface DhRunPrice {
  run_id: string;
  run_number: number | null;
  run_state: 'closed' | 'not_run';
  billing_type: string;
  run_day: string;
  cd_hours: number | null;
  days: number | null;
  footage_drilled: number | null;
  currency: string | null;
  lines: DhPriceLine[];
  subtotal: number | null;
  complete: boolean;
  problems: string[];
}

export const DH_RUN_STATES = ['open', 'closed', 'not_run'] as const;
export type DhRunState = (typeof DH_RUN_STATES)[number];

/** `dh_run_logs.billing_type` CHECK (0450). */
export const DH_RUN_BILLING_TYPES = ['per_cd_run', 'per_hour', 'per_day', 'per_foot', 'hybrid'] as const;

/** A run write body (api#902). Keys are the API's; measures IMPERIAL. */
export type DhRunWrite = Record<string, string | number | boolean | null>;

/** GET /invoices, /invoices/:id — read only; totals are STORED, never recomputed here. */
export interface DhInvoice {
  id: string;
  invoice_number: string | null;
  status: string;
  customer_id: string | null;
  customer_name: string | null;
  assembly_id: string | null;
  assembly_number: string | null;
  assembly_name: string | null;
  run_log_id: string | null;
  run_number: number | null;
  /**
   * The well job a Directional & MWD daily-ticket invoice bills (api#1090's
   * 0520). Null on a run invoice; absent from an API that predates the column
   * on this read. Both mean "not a well-job invoice".
   */
  dh_job_id?: string | null;
  job_number?: string | null;
  well_name: string | null;
  rig_name: string | null;
  subtotal: number | null;
  /** A PERCENT (DECIMAL(5,2)): 8.25 means 8.25 %. */
  tax_rate: number | null;
  tax_amount: number | null;
  total: number | null;
  /** Per invoice, CAD or USD — format every figure on it in this, not the tenant's. */
  currency: string | null;
  due_date: string | null;
  sent_at: string | null;
  paid_at: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  /** api#910 tax snapshot: the two-letter jurisdiction and the stored labels/percents/amounts. */
  jurisdiction?: string | null;
  tax_rate_1_label?: string | null;
  tax_rate_1_percent?: number | null;
  tax_rate_1_amount?: number | null;
  tax_rate_2_label?: string | null;
  tax_rate_2_percent?: number | null;
  tax_rate_2_amount?: number | null;
  /** `jurisdiction_row` | `company_columns` | `none`. */
  tax_rate_source?: string | null;
  /** Why the jurisdiction's own row was not used; `jurisdiction_absent` / `not_yet_effective` block Send. */
  tax_fallback_reason?: string | null;
  tax_exempt?: boolean | null;
  pst_exempt?: boolean | null;
}

export interface DhInvoiceLine {
  id: string;
  sort_order: number | null;
  line_type: string | null;
  description: string | null;
  component_id: string | null;
  component_serial: string | null;
  quantity: number | null;
  unit: string | null;
  rate: number | null;
  amount: number | null;
  /** The run this line bills (api#910) — what a rebill regenerates from. */
  run_log_id?: string | null;
}

export interface DhInvoiceDetail extends DhInvoice {
  line_items: DhInvoiceLine[];
}

/** POST /unified-invoices/downhole/generate (api#910). */
/**
 * POST /unified-invoices/downhole/generate takes EXACTLY ONE source (api#1090):
 * runs, a well job's signed days (`dh_job_id`), or picked day tickets
 * (`dh_ticket_ids`, 1-100). Naming two is a 400 INVOICE_SOURCE_REQUIRED, so the
 * union keeps them apart at compile time.
 */
export type DhGenerateInvoiceWrite = { due_date?: string | null; notes?: string | null } & (
  | { run_ids: string[]; dh_job_id?: never; dh_ticket_ids?: never }
  | { dh_job_id: string; run_ids?: never; dh_ticket_ids?: never }
  | { dh_ticket_ids: string[]; run_ids?: never; dh_job_id?: never }
);

/**
 * GET /downhole/jobs/:id/billing (api#1090): one well job's days between the
 * field and the bank. Every figure is the API's; the screen sums nothing.
 * `receivable` is the shared AR rule (sent invoices only, by balance).
 * `invoices[].status` is the PRESENTED status: a sent invoice past due reads
 * 'overdue' here while the invoice list still says 'sent' (pre-existing gap,
 * named in the PR).
 */
export interface DhJobBilling {
  dh_job_id: string;
  job_number: string;
  days: {
    ticketed: number;
    draft: number;
    office_approved: number;
    customer_rejected: number;
    customer_approved: number;
    signed_unbilled: number;
    signed_unbilled_total: number;
    signed_unbilled_ticket_ids: string[];
    invoiced: number;
  };
  invoices: Array<{
    id: string;
    invoice_number: string | null;
    status: string;
    currency: string | null;
    total: number | null;
    due_date: string | null;
    created_at: string;
    day_count: number;
  }>;
  billed_total: number;
  receivable: { outstanding: number | null; overdue: number | null };
  /** null when the job's days and invoices carry more than one currency. */
  currency: string | null;
  today: string;
}

/** POST /unified-invoices/:id/send → who it went to. */
export interface DhInvoiceSent {
  id: string;
  invoice_number: string | null;
  status: string;
  sent_at: string;
  sent_to: string;
  sent_to_source: 'accounting_email' | 'main_contact_email' | string;
  /** API-A (api#928). */
  sent_to_name?: string | null;
  /** What was actually copied: deduped, the `to` address dropped. */
  sent_cc?: string[];
}

/**
 * Who a downhole send goes to (API-A, lib/downhole-recipients.ts) — the same
 * resolver the send uses, so a preview and the send cannot disagree.
 */
export interface DhRecipient {
  name: string | null;
  email: string;
  source: 'entered' | 'main_contact_email' | 'accounting_email' | string;
  /** The API's own words for `source`. */
  source_label: string;
}

/** GET /downhole/tickets/:id/recipient. `to: null` comes with code NO_CUSTOMER_EMAIL — a preview, not a refusal. */
export interface DhTicketRecipientPreview {
  ticket_id: string;
  ticket_number: string;
  status: string;
  customer_name: string | null;
  to: DhRecipient | null;
  code?: 'NO_CUSTOMER_EMAIL' | string;
  message?: string;
  last_sent: { at: string; to: string | null; cc: string[] | null } | null;
}

/** GET /unified-invoices/:id/recipient (downhole only). `to: null` comes with code NO_BILLING_EMAIL. */
export interface DhInvoiceRecipientPreview {
  invoice_id: string;
  invoice_number: string | null;
  status: string;
  customer_name: string | null;
  to: DhRecipient | null;
  code?: 'NO_BILLING_EMAIL' | string;
  message?: string;
  last_sent: { at: string; to: string | null; to_source: string | null; cc: string[] | null } | null;
}

export const DH_REPORTS = ['component_usage', 'assembly_history', 'work_order_summary'] as const;
export type DhReport = (typeof DH_REPORTS)[number];

/** Reports carry the range the API actually used beside the tenant meta. */
export interface DhReportMeta extends DownholeMeta {
  report?: DhReport;
  start_date?: string;
  end_date?: string;
}

export interface DhComponentUsageRow {
  id: string;
  serial_number: string;
  category: string;
  description: string | null;
  status: string;
  runs_in_range: number;
  circulating_hours_in_range: number;
  footage_in_range: number;
  lifetime_runs: number | null;
  lifetime_run_hours: number | null;
  lifetime_footage_ft: number | null;
  wear_percent_hours: number | null;
  wear_percent_runs: number | null;
}

export interface DhAssemblyHistoryRow {
  id: string;
  assembly_number: string;
  name: string;
  status: string;
  customer_name: string | null;
  well_name: string | null;
  rig_name: string | null;
  built_at: string | null;
  laid_down_at: string | null;
  run_count: number;
  first_run_in_at: string | null;
  last_run_out_at: string | null;
  footage_drilled: number;
  circulating_hours: number;
  any_lost_in_hole: boolean | null;
}

// ─── Directional & MWD well jobs (api#1055, 0513; widths api#1059, 0516) ──────
// Every /downhole/jobs door answers 409 DD_MODE_OFF unless the company's
// dd_mwd_enabled is on. snake_case both ways, like the rest of /downhole.

export const DH_JOB_STATUSES = ['scheduled', 'mobilizing', 'on_location', 'released', 'completed', 'cancelled'] as const;
export type DhJobStatus = (typeof DH_JOB_STATUSES)[number];

/** The four instants a job's steps stamp, in order. PATCH corrects one only once reached. */
export const DH_JOB_STAMP_COLUMNS = ['mobilized_at', 'on_location_at', 'released_at', 'demobilized_at'] as const;
export type DhJobStampColumn = (typeof DH_JOB_STAMP_COLUMNS)[number];

/** Statuses POST /jobs/:id/transition can move a job TO. */
export type DhJobTransitionTarget = 'mobilizing' | 'on_location' | 'released' | 'completed' | 'cancelled';

export interface DhJob {
  id: string;
  /** 'DDJ-0001', the API's series. */
  job_number: string;
  status: string;
  customer_id: string | null;
  customer_name: string | null;
  well_name: string | null;
  uwi: string | null;
  api_number: string | null;
  lsd: string | null;
  well_licence: string | null;
  afe_number: string | null;
  po_number: string | null;
  rig_name: string | null;
  /** ISO two-letter province/state. */
  province_state: string | null;
  /** Calendar day, 'YYYY-MM-DD'. */
  scheduled_start: string | null;
  mobilized_at: string | null;
  on_location_at: string | null;
  released_at: string | null;
  demobilized_at: string | null;
  cancel_reason: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  assembly_count: number | null;
  run_count: number | null;
  /** A run on this job is in the hole (run in, not out). */
  run_open: boolean | null;
  /** api#1062: bill footage per day (needs that day's depths). Absent before batch 4: unknown. */
  footage_billing?: boolean;
}

/** GET /jobs/:id: the job plus its assemblies and runs, in the list reads' shapes. */
export interface DhJobDetail extends DhJob {
  assemblies: DhAssembly[];
  runs: DhRun[];
  /** api#1057. Optional: a response from before batch 3 carries none, which is not "no crew". */
  crew?: DhCrewSpell[];
}

/**
 * POST /jobs and PATCH /jobs/:id. undefined = not sent; null clears. The API
 * refuses unknown keys (UNKNOWN_FIELD), derived keys (COMPUTED_FIELD), and on
 * a closed job every key but `notes` (JOB_CLOSED).
 */
export interface DhJobWrite {
  customer_id?: string;
  well_name?: string;
  uwi?: string | null;
  api_number?: string | null;
  lsd?: string | null;
  well_licence?: string | null;
  afe_number?: string | null;
  po_number?: string | null;
  rig_name?: string | null;
  province_state?: string | null;
  scheduled_start?: string | null;
  notes?: string | null;
  /** api#1062. Changing it sends every day's ticket back to draft. */
  footage_billing?: boolean;
  /** PATCH only, and only for a stamp already reached. Tenant wall clock or ISO. */
  mobilized_at?: string;
  on_location_at?: string;
  released_at?: string;
  demobilized_at?: string;
}

/**
 * POST /jobs/:id/transition. `at` defaults to now and is read on the tenant's
 * clock when it carries no offset; cancelling takes a reason and NO `at`.
 */
export type DhJobTransitionWrite =
  | { status: Exclude<DhJobTransitionTarget, 'cancelled'>; at?: string }
  | { status: 'cancelled'; cancel_reason: string };

// ─── Directional & MWD crew and personnel rates (api#1057, 0514) ─────────────
// Both behind the dd_mwd mode (409 DD_MODE_OFF otherwise). Customer BILLING
// rates only; pay is a later batch.

export const DH_PERSONNEL_ROLES = ['directional_driller', 'mwd_operator', 'dd_trainee', 'mwd_trainee', 'coordinator'] as const;
export type DhPersonnelRole = (typeof DH_PERSONNEL_ROLES)[number];

export const DH_CREW_SHIFTS = ['day', 'night'] as const;
export type DhCrewShift = (typeof DH_CREW_SHIFTS)[number];

/**
 * One spell a person spends on a well job, in one role. A hand rotating off
 * and back on is two spells. `off_at` null means still on.
 */
export interface DhCrewSpell {
  id: string;
  dh_job_id: string;
  user_id: string;
  person_name: string | null;
  personnel_role: string;
  shift: string | null;
  on_at: string;
  off_at: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

/** POST /jobs/:id/crew; PATCH takes the same minus user_id (COMPUTED_FIELD). Times on the tenant clock. */
export interface DhCrewWrite {
  user_id?: string;
  personnel_role?: string;
  shift?: string | null;
  on_at?: string;
  /** null clears it: the person is still on. */
  off_at?: string | null;
  notes?: string | null;
}

/**
 * What a role bills the customer per day. A figure left out is NOT QUOTED, not
 * $0. `customer_id` null is the company default. Days inclusive; no expiry is
 * open-ended.
 */
export interface DhPersonnelRate {
  id: string;
  customer_id: string | null;
  customer_name: string | null;
  personnel_role: string;
  day_rate: number | null;
  standby_day_rate: number | null;
  travel_day_rate: number | null;
  currency: string;
  effective_date: string;
  expiry_date: string | null;
  notes: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface DhPersonnelRateWrite {
  customer_id?: string | null;
  personnel_role?: string;
  day_rate?: number | null;
  standby_day_rate?: number | null;
  travel_day_rate?: number | null;
  currency?: 'CAD' | 'USD';
  effective_date?: string;
  expiry_date?: string | null;
  notes?: string | null;
}

// ─── Directional & MWD job days (api#1062, 0515) ─────────────────────────────
// A well job bills by the day. Every figure here is the API's: the screen
// prints quantities, rates, amounts, subtotals and ticket totals as returned
// and computes none of them. An absent figure is null and prints "—".

export type DhDayKind = 'mob' | 'on_location' | 'demob';
export const DH_DAY_STATES = ['operating', 'standby', 'off'] as const;
export type DhDayState = (typeof DH_DAY_STATES)[number];

/** One row of GET /jobs/:id/days: mobilization day to demob (or today). */
export interface DhJobDay {
  work_date: string;
  day_kind: string;
  depth_start_ft: number | null;
  depth_end_ft: number | null;
  /** The office's override; null = the API decides the state from the runs. */
  state_override: string | null;
  override_reason: string | null;
  ticket_id: string | null;
  ticket_number: string | null;
  ticket_status: string | null;
  ticket_total: number | null;
  /**
   * The live (non-void) invoice this day's ticket is on (api#1090), null when
   * unbilled. ABSENT (an API from before #1090) is "not known", not "unbilled".
   */
  invoice_id?: string | null;
  invoice_number?: string | null;
}

export interface DhDayLine {
  line_type: string;
  unit: string;
  description: string;
  quantity: number | null;
  rate: number | null;
  /** null = unpriced; `unpriced_reason` says why. Never a $0. */
  amount: number | null;
  currency: string | null;
  dh_job_crew_id: string | null;
  user_id: string | null;
  personnel_role: string | null;
  assembly_id: string | null;
  component_id: string | null;
  serial_number: string | null;
  rate_id: string | null;
  unpriced_reason?: string;
  not_on_quote?: true;
}

/** GET /jobs/:id/days/:date: the live price, plus the day's own inputs and ticket. */
export interface DhDayPrice {
  dh_job_id: string;
  work_date: string;
  day_kind: string;
  state: string | null;
  state_overridden: boolean;
  /** Feet drilled that day, the API's figure. */
  footage: number | null;
  lines: DhDayLine[];
  currency: string | null;
  /** null unless `complete`. */
  subtotal: number | null;
  complete: boolean;
  /** Why the day cannot be priced, in the API's words. Shown verbatim. */
  problems: string[];
  depth_start_ft: number | null;
  depth_end_ft: number | null;
  state_override: string | null;
  override_reason: string | null;
  ticket: { id: string; ticket_number: string; status: string } | null;
}

/** PUT /jobs/:id/days/:date. Clearing the override clears its reason too. */
export interface DhDayWrite {
  depth_start_ft?: number | null;
  depth_end_ft?: number | null;
  state_override?: DhDayState | null;
  override_reason?: string | null;
}
